import React, { useRef, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

interface DropdownMenuProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  placement?: "bottom" | "bottom-end" | "right";
  /** Outside-mousedowns this returns true for don't close the menu —
   *  e.g. clicks inside a tooltip-portal popover spawned from a row. */
  shouldIgnoreOutsideEvent?: (e: MouseEvent) => boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  anchorRef,
  isOpen,
  onClose,
  children,
  className,
  style,
  placement = "bottom",
  shouldIgnoreOutsideEvent,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedTop, setAdjustedTop] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (shouldIgnoreOutsideEvent?.(e)) return;
      if (
        !anchorRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, anchorRef, onClose, shouldIgnoreOutsideEvent]);

  // For "right" placement: center menu on the anchor, clamped to viewport
  useLayoutEffect(() => {
    if (
      !isOpen ||
      placement !== "right" ||
      !menuRef.current ||
      !anchorRef.current
    ) {
      setAdjustedTop(null);
      return;
    }
    const anchorRect = anchorRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const anchorCenter = anchorRect.top + anchorRect.height / 2;
    const margin = 8;
    const top = Math.max(
      margin,
      Math.min(
        anchorCenter - menuHeight / 2,
        window.innerHeight - menuHeight - margin,
      ),
    );
    setAdjustedTop(top);
  }, [isOpen, placement, anchorRef, children]);

  if (!isOpen || !anchorRef.current) return null;

  const rect = anchorRef.current.getBoundingClientRect();

  let positionStyle: React.CSSProperties;
  if (placement === "right") {
    positionStyle = { top: adjustedTop ?? rect.top, left: rect.right + 8 };
  } else if (placement === "bottom-end") {
    // Right-aligned: anchor the menu's right edge to the trigger's right edge,
    // content-sized, so it grows leftward and never clips the viewport's right.
    positionStyle = {
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    };
  } else {
    positionStyle = {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
  }

  return createPortal(
    <div
      ref={menuRef}
      className={`menu-surface fixed z-9999 ${className ?? ""}`}
      style={{ ...positionStyle, ...style }}
    >
      {children}
    </div>,
    document.body,
  );
};
